import React, { useEffect, useMemo, useState } from 'react';
import {
  Form,
  Input,
  Modal,
  Spin,
  message,
} from 'antd';
import { CheckCircleFilled, CheckOutlined, WarningOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import OnTraccrButton from '../common/buttons/OnTraccrButton';
import WorkflowMarketplaceExportChecklist from './WorkflowMarketplaceExportChecklist';
import Colors from '../constants/Colors';
import {
  exportWorkflowMarketplaceWorkflow,
  getWorkflowMarketplaceExportOptions,
} from './state/workflowMarketplace.actions';

// Mirrors the backend rule: the listing name is also its S3 folder
const INVALID_NAME_CHARACTERS = /[/\\+?#%]/;
const DUPLICATE_NAME_MESSAGE = 'A listing with this name already exists';

// [key, singular, plural] for review step count pills
// skip empty keys
const SUMMARY_LABELS = [
  ['boards', 'board', 'boards'],
  ['cardTypes', 'card type', 'card types'],
  ['boardStatuses', 'status', 'statuses'],
  ['forms', 'form', 'forms'],
  ['boardFormMappings', 'board form mapping', 'board form mappings'],
  ['cardTriggers', 'trigger', 'triggers'],
  ['formCustomTables', 'custom table', 'custom tables'],
  ['formStatuses', 'form status', 'form statuses'],
];

const STEPS = {
  SELECT: 'select',
  REVIEW: 'review',
  DONE: 'done',
};

// Boards carry divisionIds, forms a single divisionId
const divisionLabel = (divisions, { divisionIds, divisionId }) => (
  (divisionIds ?? [divisionId])
    .map((id) => divisions?.[id]?.name)
    .filter(Boolean)
    .join(', ')
);

export default function WorkflowMarketplaceExportModal({ open, onClose }) {
  const dispatch = useDispatch();
  const divisions = useSelector((state) => state.settings.divisions);

  const [step, setStep] = useState(STEPS.SELECT);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(null);
  const [options, setOptions] = useState({ boards: [], forms: [] });
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selectedBoardIds, setSelectedBoardIds] = useState([]);
  const [selectedFormIds, setSelectedFormIds] = useState([]);
  const [review, setReview] = useState(null);
  const [listing, setListing] = useState(null); // final listing name
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setStep(STEPS.SELECT);
    setName('');
    setNameError(null);
    setSelectedBoardIds([]);
    setSelectedFormIds([]);
    setReview(null);
    setListing(null);
    setLoadingOptions(true);
    const loadOptions = async () => {
      const data = await dispatch(getWorkflowMarketplaceExportOptions());
      if (cancelled) return;
      setOptions({ boards: data?.boards ?? [], forms: data?.forms ?? [] });
      setLoadingOptions(false);
    };
    loadOptions();
    return () => { cancelled = true; };
  }, [open]);

  const boardItems = useMemo(() => options.boards.map((board) => ({
    ...board, label: board.title, division: divisionLabel(divisions, board),
  })), [options.boards, divisions]);

  const formItems = useMemo(() => options.forms.map((form) => ({
    ...form, label: (form.name ?? '').trim(), division: divisionLabel(divisions, form),
  })), [options.forms, divisions]);

  const trimmedName = name.trim();
  const hasInvalidCharacters = INVALID_NAME_CHARACTERS.test(trimmedName);
  const nameHelp = hasInvalidCharacters
    ? 'Listing names cannot contain / \\ + ? # or %'
    : nameError;
  const hasSelection = selectedBoardIds.length > 0 || selectedFormIds.length > 0;
  const canReview = !!trimmedName && !hasInvalidCharacters && hasSelection && !loadingOptions;

  const runExport = async (dryRun) => {
    setSubmitting(true);
    const { data, error } = await dispatch(exportWorkflowMarketplaceWorkflow({
      name: trimmedName,
      ...(selectedBoardIds.length ? { boardIds: selectedBoardIds } : {}),
      ...(selectedFormIds.length ? { formTemplateIds: selectedFormIds } : {}),
      dryRun,
    }));
    setSubmitting(false);
    if (error) {
      if (error.statusCode === 409) {
        setNameError(DUPLICATE_NAME_MESSAGE);
        setStep(STEPS.SELECT);
      } else {
        message.error(error.message || 'Could not export to the portal');
      }
      return;
    }
    if (dryRun) {
      setReview(data);
      setStep(STEPS.REVIEW);
    } else {
      setListing(data.listing);
      setStep(STEPS.DONE);
    }
  };

  const renderSelect = () => (
    <>
      <div className="workflow-marketplace-import-modal-title">Export to portal</div>
      <div className="workflow-marketplace-import-modal-description">
        Export boards and forms from this account as a draft listing.
        It stays hidden until it is published in the listing portal.
      </div>
      <Form layout="vertical">
        <Form.Item
          label="Listing name"
          required
          validateStatus={nameHelp ? 'error' : undefined}
          help={nameHelp}
          className="workflow-marketplace-import-modal-division-selector-form-item"
        >
          <Input
            value={name}
            maxLength={255}
            placeholder="e.g. Bid & Project Tracking"
            onChange={(e) => {
              setName(e.target.value);
              setNameError(null);
            }}
          />
        </Form.Item>
      </Form>
      <Spin spinning={loadingOptions}>
        <div className="workflow-marketplace-export-modal-checklists">
          <WorkflowMarketplaceExportChecklist
            title="Boards"
            items={boardItems}
            selectedIds={selectedBoardIds}
            onChange={setSelectedBoardIds}
          />
          <WorkflowMarketplaceExportChecklist
            title="Forms"
            items={formItems}
            selectedIds={selectedFormIds}
            onChange={setSelectedFormIds}
          />
        </div>
      </Spin>
      <div className="workflow-marketplace-import-modal-buttons">
        <OnTraccrButton title="Cancel" type="cancel" onClick={onClose} disabled={submitting} />
        <OnTraccrButton
          title="Review"
          onClick={() => runExport(true)}
          loading={submitting}
          disabled={!canReview}
        />
      </div>
    </>
  );

  const renderReview = () => {
    const { summary = {}, warnings = [] } = review ?? {};
    return (
      <>
        <div className="workflow-marketplace-import-modal-title">Review export</div>
        <div className="workflow-marketplace-import-modal-description">
          <b>{trimmedName}</b>
          <span> will be created as a draft listing in the portal.</span>
        </div>
        <div className="workflow-marketplace-export-modal-summary">
          {SUMMARY_LABELS
            .filter(([key]) => summary[key])
            .map(([key, singular, plural]) => (
              <span key={key} className="workflow-marketplace-export-modal-summary-pill">
                {summary[key]} {summary[key] === 1 ? singular : plural}
              </span>
            ))}
        </div>
        <div className="workflow-marketplace-export-modal-warnings">
          <div className="workflow-marketplace-export-modal-warnings-header">
            {warnings.length ? (
              <WarningOutlined className="workflow-marketplace-import-modal-warning-icon" />
            ) : (
              <CheckCircleFilled style={{ color: Colors.GREEN_600, fontSize: 20 }} />
            )}
            <span>
              {warnings.length
                ? `${warnings.length} ${warnings.length === 1 ? 'item was' : 'items were'} removed or need review`
                : 'Nothing was removed'}
            </span>
          </div>
          {warnings.length > 0 && (
            <div className="workflow-marketplace-export-modal-warnings-list">
              {warnings.map((warning, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${index}-${warning.entity}-${warning.path}`}
                  style={{ paddingBottom: 2 }}
                >
                  <div className="workflow-marketplace-export-modal-warning-entity">{warning.entity}</div>
                  <div className="workflow-marketplace-export-modal-muted">{warning.message}</div>
                </div>
              ))}
            </div>
          )}
          <div
            className="workflow-marketplace-export-modal-muted"
            style={{ paddingTop: 12, borderTop: '1px solid #E5E7EB' }}
          >
            Customer-specific text (names, emails, options) is not changed.
            Follow the sanitization SOP in the listing portal before publishing.
          </div>
        </div>
        <div className="workflow-marketplace-import-modal-buttons">
          <OnTraccrButton
            title="Back"
            type="cancel"
            onClick={() => setStep(STEPS.SELECT)}
            disabled={submitting}
          />
          <OnTraccrButton
            title="Create draft"
            onClick={() => runExport(false)}
            loading={submitting}
          />
        </div>
      </>
    );
  };

  const renderDone = () => (
    <>
      <div className="workflow-marketplace-import-modal-title">Export to portal</div>
      <div className="workflow-marketplace-export-modal-done">
        <div className="workflow-marketplace-export-modal-done-icon">
          <CheckOutlined />
        </div>
        <div className="workflow-marketplace-export-modal-done-title">Draft created</div>
        <p className="workflow-marketplace-export-modal-done-description">
          <b>{listing?.name ?? trimmedName}</b> is now a draft in the workflow listing portal.
          Add the description, screenshots and labels there, then publish it.
        </p>
      </div>
      <div className="workflow-marketplace-export-modal-done-buttons">
        <OnTraccrButton title="Done" onClick={onClose} />
      </div>
    </>
  );

  if (!open) return null;

  const content = {
    [STEPS.SELECT]: renderSelect,
    [STEPS.REVIEW]: renderReview,
    [STEPS.DONE]: renderDone,
  }[step];

  return (
    <Modal
      visible
      onCancel={submitting ? undefined : onClose}
      footer={null}
      width={840}
      centered
      closable={!submitting}
      keyboard={!submitting}
      maskClosable={!submitting}
      wrapClassName="workflow-marketplace-export-modal-wrap"
      className="workflow-marketplace-import-modal workflow-marketplace-export-modal"
    >
      {content()}
    </Modal>
  );
}

WorkflowMarketplaceExportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
